import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { manualSerialProblem, normalizeManualSerial } from '@siliconx/shared/dto/client-asset';
import { Button } from '@siliconx/shared/ui/Button';
import { api, throwIfNotOk } from '../../lib/api';
import { clearClientIdentity } from '../../lib/auth';
import { qk } from '../../lib/queryKeys';
import { MaskedSerial } from './MaskedSerial';

/**
 * One-time manual serial entry after a TrueRead probe (individual orgs,
 * migration 929). Two states, both driven by the server:
 *
 *  - `serialEditable`: a highlighted callout with the field. The server decides
 *    (client_asset_serial_editable) - this component never re-derives
 *    eligibility. Entry is a two-step (type, then confirm) because it can be
 *    done ONCE; only SiliconX support can correct it afterwards.
 *  - `serialEntered`: read-only masked value (SIL-1269: Reveal to view) labelled "Entered by you
 *    (unverified)". The value is never presented as verified: it is not the
 *    probe's reading and does not establish the asset's Fingerprint.
 *
 * Renders nothing otherwise (business orgs, ineligible assets, non-admins).
 */
export function SerialEntryCard({
  assetId,
  serialEditable,
  serialEntered,
  canEdit,
}: {
  assetId: string;
  serialEditable: boolean;
  /** True once the user has typed a serial (asset.serial_entered_at); the value itself is fetched on Reveal. */
  serialEntered: boolean;
  canEdit: boolean;
}) {
  const queryClient = useQueryClient();
  const [value, setValue] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: async (serial: string) => {
      const res = await api.api.client.assets[':assetId'].serial.$put({
        param: { assetId },
        json: { serial },
      });
      await throwIfNotOk(res);
    },
    onSuccess: () => {
      setError(null);
      setConfirming(false);
      void queryClient.invalidateQueries({ queryKey: qk.assets.detail(assetId) });
      // The "add your serial" nudges read the pending count off the identity.
      clearClientIdentity();
      void queryClient.invalidateQueries({ queryKey: qk.identity() });
    },
    onError: (err: Error) => {
      setConfirming(false);
      setError(err.message || 'Could not save the serial number.');
    },
  });

  if (serialEntered) {
    return (
      <div className="rounded-lg border border-border bg-card px-4 py-3" data-testid="serial-entered">
        <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Serial number</p>
        <p className="mt-1 font-mono text-sm text-foreground">
          <MaskedSerial assetId={assetId} hasSerial />
        </p>
        <p className="mt-1 text-xs text-muted-foreground">
          Entered by you (unverified). Contact support if it needs correcting.
        </p>
      </div>
    );
  }

  if (!serialEditable || !canEdit) return null;

  function handleReview() {
    const problem = manualSerialProblem(value);
    if (problem) {
      setError(problem);
      return;
    }
    setError(null);
    setConfirming(true);
  }

  const normalized = normalizeManualSerial(value);

  return (
    <div className="rounded-lg border border-brand-200 bg-brand-50 p-4" data-testid="serial-entry">
      <p className="text-sm font-semibold text-foreground">Add this asset&apos;s serial number</p>
      <p className="mt-1 text-xs text-muted-foreground">
        TrueRead could not read a serial for this asset. You can enter it{' '}
        <span className="font-semibold">once</span>. After that you cannot change it - SiliconX support must
        correct it.
      </p>

      {!confirming ? (
        <div className="mt-3 flex flex-wrap items-start gap-2">
          <input
            type="text"
            aria-label="Serial number"
            value={value}
            maxLength={64}
            autoComplete="off"
            spellCheck={false}
            onChange={(e) => {
              setValue(e.target.value);
              setError(null);
            }}
            className="min-w-[16rem] rounded-md border border-border bg-background px-3 py-1.5 font-mono text-sm"
            placeholder="e.g. 1234567890"
          />
          <Button type="button" onClick={handleReview} disabled={value.trim() === ''}>
            Review
          </Button>
        </div>
      ) : (
        <div className="mt-3 space-y-2" role="alertdialog" aria-label="Confirm serial number">
          <p className="text-sm text-foreground">
            Save <span className="font-mono font-semibold">{normalized}</span> as this asset&apos;s serial number?
            You cannot change it afterwards.
          </p>
          <div className="flex gap-2">
            <Button type="button" onClick={() => mutation.mutate(normalized)} disabled={mutation.isPending}>
              {mutation.isPending ? 'Saving...' : 'Confirm and save'}
            </Button>
            <Button type="button" variant="secondary" onClick={() => setConfirming(false)} disabled={mutation.isPending}>
              Back
            </Button>
          </div>
        </div>
      )}

      {error && (
        <p role="alert" className="mt-2 text-xs text-red-600">
          {error}
        </p>
      )}
    </div>
  );
}
